<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="page-header tw-mb-8">
          <div>
            <h2 class="page-title">Digital Certificates</h2>
            <p class="page-subtitle">Search and download policy digital certificates via policy number or registration number.</p>
          </div>
        </header>

        <!-- Search Form -->
        <div class="search-card tw-mb-6">
          <h3 class="search-card-title">Search Certificate</h3>
          <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-items-end">
            <div class="tw-w-full sm:tw-max-w-[420px]">
              <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Policy or Reg Number</label>
              <div class="search-wrapper" :class="{ focused: inputFocused }">
                <i class="ph ph-magnifying-glass search-icon"></i>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="e.g. 010/0070/156049/2026 or KBC 307"
                  class="search-input"
                  @focus="inputFocused = true"
                  @blur="inputFocused = false"
                  @keyup.enter="searchCertificate"
                />
                <button type="button" v-if="searchQuery" class="search-clear" @click="clearSearch">
                  <i class="ph ph-x"></i>
                </button>
              </div>
            </div>
            <v-btn
              color="primary"
              height="48"
              class="tw-px-8"
              :loading="loading"
              :disabled="!searchQuery.trim()"
              @click="searchCertificate"
            >
              Search
            </v-btn>
          </div>
        </div>

        <!-- Success State -->
        <transition name="slide-up">
          <div v-if="searchResult && !loading">

            <!-- Certificate Card -->
            <div class="cert-card tw-mb-4">
              <div class="cert-card-left">
                <div class="cert-icon-wrap">
                  <v-icon size="32" color="#16A34A">mdi-shield-check</v-icon>
                </div>
                <div>
                  <p class="cert-label">{{ searchResultIsPa ? 'Policy Document Found' : 'Certificate Found' }}</p>
                  <p class="cert-policy-no">{{ searchResult.policyNumber }}</p>
                  <p class="cert-batch">Batch No: <strong>{{ searchResult.batchNo }}</strong></p>
                  <p v-if="searchResult.registrationNumber" class="cert-batch tw-mt-1">
                    Reg No: <strong class="tw-text-[#1E4276]">{{ searchResult.registrationNumber }}</strong>
                  </p>
                </div>
              </div>
              <div class="cert-card-actions">
                <v-btn
                  color="#E53E3E"
                  dark
                  height="44"
                  class="tw-px-5"
                  @click="openCertificate"
                >
                  <v-icon left size="18">mdi-file-pdf-box</v-icon>
                  {{ searchResultIsPa ? 'View Policy Document' : 'View Certificate' }}
                </v-btn>
                <v-btn
                  outlined
                  color="primary"
                  height="44"
                  class="tw-px-5"
                  @click="downloadCertificate"
                >
                  <v-icon left size="18">mdi-download</v-icon>
                  Download
                </v-btn>
              </div>
            </div>

            <!-- Info Helper -->
            <div class="info-helper">
              <v-icon small color="#0284C7" class="tw-flex-shrink-0">mdi-information-outline</v-icon>
              <p class="info-helper-text">
                Certificates are generated automatically after policy issuance. If not available yet, it may still be processing — please wait a few minutes and try again, or contact underwriting.
              </p>
            </div>

          </div>
        </transition>

        <!-- Error State -->
        <transition name="slide-up">
          <div v-if="errorMsg && !loading" class="error-state">
            <div class="error-icon-wrap">
              <v-icon size="28" color="#DC2626">mdi-alert-circle-outline</v-icon>
            </div>
            <div>
              <p class="error-title">No Certificate Found</p>
              <p class="error-body">{{ errorMsg }}</p>
            </div>
          </div>
        </transition>

        <!-- Empty State (Only if no search and no certificates) -->
        <div v-if="!searchResult && !errorMsg && !loading && !certificates.length && !tableLoading" class="empty-state">
          <div class="empty-icon-ring">
            <v-icon size="38" color="#94A3B8">mdi-certificate-outline</v-icon>
          </div>
          <p class="empty-title">No certificates found</p>
          <p class="empty-subtitle">Enter a policy number above or wait for your recent certificates to load</p>
        </div>

        <!-- Recent Certificates Table -->
        <div v-if="certificates.length || tableLoading" class="tw-mt-10">
          <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <h3 class="tw-text-lg tw-font-bold tw-text-slate-800">Your Recent Certificates</h3>
            <v-chip v-if="totalCertificates > 0" small color="primary" outlined label>
              {{ totalCertificates }} Total
            </v-chip>
          </div>

          <v-data-table
            :headers="headers"
            :items="certificates"
            :loading="tableLoading"
            :server-items-length="totalCertificates"
            :options.sync="tableOptions"
            :footer-props="{
              'items-per-page-options': [5, 10, 20],
              'show-first-last-page': true
            }"
            class="cert-data-table"
            @update:options="fetchAgentCertificates"
          >
            <template #progress>
              <div class="certificate-shimmer-table">
                <div
                  v-for="n in 6"
                  :key="`certificate-shimmer-${n}`"
                  class="certificate-shimmer-row"
                >
                  <div class="shimmer shimmer-policy"></div>

                  <div class="shimmer shimmer-reg"></div>

                  <div class="certificate-shimmer-client">
                    <div class="shimmer shimmer-avatar"></div>
                    <div class="shimmer shimmer-client-name"></div>
                  </div>

                  <div class="shimmer shimmer-product"></div>

                  <div class="certificate-shimmer-dates">
                    <div class="shimmer shimmer-date"></div>
                    <div class="shimmer shimmer-date shimmer-date-second"></div>
                  </div>

                  <div class="certificate-shimmer-actions">
                    <div class="shimmer shimmer-action"></div>
                    <div class="shimmer shimmer-action"></div>
                  </div>
                </div>
              </div>
            </template>
              <!-- Policy Number Column -->
              <template #[`item.policyNumber`]="{ item }">
                <span class="tw-font-bold tw-text-[#1E4276]">{{ item.policyNumber }}</span>
              </template>

              <!-- Registration Number Column -->
              <template #[`item.registrationNumber`]="{ item }">
                <span class="tw-text-xs tw-font-semibold tw-text-gray-600">{{ item.registrationNumber || '—' }}</span>
              </template>

              <!-- Client Column -->
              <template #[`item.clientName`]="{ item }">
                <div class="tw-flex tw-items-center">
                  <v-avatar size="24" color="#F1F5F9" class="tw-mr-2">
                    <v-icon size="14" color="#64748B">mdi-account</v-icon>
                  </v-avatar>
                  <span class="tw-text-sm tw-text-slate-700">{{ item.clientName || 'N/A' }}</span>
                </div>
              </template>

              <!-- Dates Column -->
              <template #[`item.dates`]="{ item }">
                <div class="tw-text-xs tw-text-slate-500">
                  <div class="tw-flex tw-items-center">
                    <v-icon size="12" class="tw-mr-1">mdi-calendar-export</v-icon>
                    {{ formatDate(item.coverFrom) }}
                  </div>
                  <div class="tw-flex tw-items-center tw-mt-1">
                    <v-icon size="12" class="tw-mr-1">mdi-calendar-import</v-icon>
                    {{ formatDate(item.coverTo) }}
                  </div>
                </div>
              </template>

              <!-- Actions Column -->
              <template #[`item.actions`]="{ item }">
                <div class="tw-flex tw-items-center tw-gap-2">
                  <template v-if="item.certReady">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="#E53E3E"
                          v-bind="attrs"
                          v-on="on"
                          @click="openCertFromTable(item)"
                        >
                          <v-icon size="20">mdi-file-pdf-box</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ item.isPa ? 'View Policy Document' : 'View Certificate' }}</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="downloadCertFromTable(item)"
                        >
                          <v-icon size="20">mdi-download</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ item.isPa ? 'Download Policy Document' : 'Download Certificate' }}</span>
                    </v-tooltip>
                  </template>

                  <v-tooltip v-else bottom max-width="220">
                    <template #activator="{ on, attrs }">
                      <v-chip
                        x-small
                        color="#FEF3C7"
                        text-color="#92400E"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon x-small left>mdi-clock-outline</v-icon>
                        Not Ready
                      </v-chip>
                    </template>
                    <span>{{ item.isPa ? 'Policy document is still processing.' : 'Certificate is still processing. Check back shortly.' }}</span>
                  </v-tooltip>
                </div>
              </template>

              <!-- No Data State -->
              <template #no-data>
                <div class="tw-py-8 tw-text-slate-400">
                  <v-icon size="40" color="#E2E8F0" class="tw-mb-2">mdi-database-off</v-icon>
                  <p>No certificates found for your account.</p>
                </div>
              </template>
            </v-data-table>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import axios from 'axios';
import AgentDefault from '@/components/layout/AgentDefault';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import {
  downloadPolicyDocument,
  isPaProduct,
  openPolicyDocument,
  resolvePaPolicyDocument,
} from '@/utils/policyDocumentsApi';

export default {
  name: 'AgentCertificates',
  components: { AgentDefault },
  data() {
    return {
      searchQuery: '',
      inputFocused: false,
      loading: false,
      searchResult: null,
      errorMsg: null,
      pdfUrl: '',
      policyDocument: null,
      searchResultIsPa: false,

      // Listing state
      certificates: [],
      totalCertificates: 0,
      tableLoading: false,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      headers: [
        { text: 'Policy Number', value: 'policyNumber', sortable: false },
        { text: 'Reg No', value: 'registrationNumber', sortable: false },
        { text: 'Client', value: 'clientName', sortable: false },
        { text: 'Product', value: 'productName', sortable: false },
        { text: 'Validity', value: 'dates', sortable: false },
        {
          text: 'Actions', value: 'actions', sortable: false, align: 'end', width: '100px',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
  },
  mounted() {
    this.fetchAgentCertificates();
  },
  methods: {
    clearSearch() {
      trackAgentInteraction(this, 'certificates_clear_search');
      this.searchQuery = '';
      this.searchResult = null;
      this.errorMsg = null;
      this.pdfUrl = '';
      this.policyDocument = null;
      this.searchResultIsPa = false;
      this.fetchAgentCertificates();
    },

    async searchCertificate() {
      if (!this.searchQuery.trim()) return;
      trackAgentInteraction(this, 'certificates_search', {
        query: this.searchQuery.trim(),
      });
      this.loading = true;
      this.searchResult = null;
      this.errorMsg = null;
      this.pdfUrl = '';
      this.policyDocument = null;
      this.searchResultIsPa = false;

      try {
        const res = await axios.get(
          `/customer/quotation/policy/details?policyNumber=${encodeURIComponent(this.searchQuery.trim())}`,
          { validateStatus: () => true, _skipErrorRedirect: true },
        );

        if (!(res.status === 200 && res.data?.success)) {
          this.errorMsg = res.data?.message || res.data?.msg || 'Policy not found. Please double-check the policy number.';
          return;
        }

        const payload = res.data.data || res.data.object;
        if (!payload?.policyNumber) {
          this.errorMsg = 'Policy not found. Please double-check the policy number.';
          return;
        }

        if (isPaProduct(payload)) {
          const docResult = await resolvePaPolicyDocument(payload.policyNumber);
          if (docResult.ready && docResult.document) {
            this.searchResult = payload;
            this.searchResultIsPa = true;
            this.policyDocument = docResult.document;
            return;
          }
          this.errorMsg = 'Policy found but the policy document is not ready yet. It may still be processing — please wait a few minutes and try again.';
          return;
        }

        if (!payload?.batchNo) {
          const paFallback = await this.tryPaPolicyDocument(payload);
          if (paFallback) return;
          this.errorMsg = 'This policy does not have an associated batch number for certificate generation yet.';
          return;
        }

        const certRes = await axios.get(
          `/customer/gis/policies/certificate/pdf/batch/${payload.batchNo}`,
          { validateStatus: () => true, _skipErrorRedirect: true },
        );

        if (certRes.status === 200 && certRes.data?.success && certRes.data?.pdfUrl) {
          this.searchResult = payload;
          this.searchResultIsPa = false;
          this.pdfUrl = certRes.data.pdfUrl;

          // Filter the certificates table to show just this specific one if it exists
          const existingItem = this.certificates.find((c) => c.policyNumber === payload?.policyNumber);
          if (existingItem) {
            this.certificates = [existingItem];
            this.totalCertificates = 1;
          } else {
            // Mock an item for the table if it's not currently loaded in the recent list
            const matchedItem = {
              policyNumber: payload?.policyNumber,
              batchNo: payload?.batchNo,
              registrationNumber: payload?.registrationNumber,
              clientName: payload?.clientName || 'N/A',
              productName: payload?.productName || 'N/A',
              coverFrom: payload?.coverFrom,
              coverTo: payload?.coverTo,
              pdfUrl: certRes.data.pdfUrl,
              certReady: true,
            };
            this.certificates = [matchedItem];
            this.totalCertificates = 1;
          }

          return;
        }

        // Certificate not ready — try PA DocuWare when motor cert is unavailable
        const paFallback = await this.tryPaPolicyDocument(payload);
        if (paFallback) return;

        // Certificate not ready — parse error if present
        if (certRes.data?.errors?.length > 0) {
          const { errorCode, errorText } = certRes.data.errors[0];
          if (errorCode === 'NOT_FOUND') {
            this.errorMsg = 'Policy found but the certificate is not ready yet. It may still be processing — please wait a few minutes and try again.';
          } else {
            this.errorMsg = errorText || 'Certificate could not be retrieved at this time.';
          }
          return;
        }

        this.errorMsg = 'Certificate generation failed. Please try again later or contact support.';
      } catch {
        this.errorMsg = 'An error occurred while communicating with the server.';
      } finally {
        this.loading = false;
      }
    },

    async tryPaPolicyDocument(payload) {
      if (!payload?.policyNumber) return false;
      const looksNonMotor = isPaProduct(payload)
        || !payload.registrationNumber
        || String(payload.productName || '').toUpperCase().includes('ACCIDENT');
      if (!looksNonMotor) return false;
      const docResult = await resolvePaPolicyDocument(payload.policyNumber);
      if (!docResult.ready || !docResult.document) return false;
      this.searchResult = payload;
      this.searchResultIsPa = true;
      this.policyDocument = docResult.document;
      return true;
    },

    async openCertificate() {
      if (this.searchResultIsPa && this.policyDocument) {
        trackAgentInteraction(this, 'certificates_open_policy_document', {
          policy_number: this.searchResult?.policyNumber || 'unknown',
        });
        openPolicyDocument(this.policyDocument).then((result) => {
          if (!result.success && this.$toast) {
            this.$toast.error(result.message || 'Could not open policy document');
          }
        });
        return;
      }
      if (this.pdfUrl) {
        trackAgentInteraction(this, 'certificates_open_certificate', {
          policy_number: this.searchResult?.policyNumber || 'unknown',
        });
        window.open(this.pdfUrl, '_blank', 'noopener,noreferrer');
      }
    },

    async downloadCertificate() {
      if (this.searchResultIsPa && this.policyDocument) {
        trackAgentInteraction(this, 'certificates_download_policy_document', {
          policy_number: this.searchResult?.policyNumber || 'unknown',
        });
        const result = await downloadPolicyDocument(
          this.policyDocument,
          this.searchResult?.policyNumber,
        );
        if (!result.success && this.$toast) {
          this.$toast.error(result.message || 'Could not download policy document');
        }
        return;
      }
      if (!this.pdfUrl) return;
      trackAgentInteraction(this, 'certificates_download_certificate', {
        policy_number: this.searchResult?.policyNumber || 'unknown',
      });
      // Force download via anchor tag
      const a = document.createElement('a');
      a.href = this.pdfUrl;
      a.download = `Certificate_${this.searchResult.policyNumber}.pdf`;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    async resolveCertificateAvailability(item) {
      if (isPaProduct(item)) {
        if (!item.policyNumber) {
          return {
            ...item, pdfUrl: null, certReady: false, isPa: true,
          };
        }
        try {
          const docResult = await resolvePaPolicyDocument(item.policyNumber);
          return {
            ...item,
            isPa: true,
            policyDocument: docResult.document,
            pdfUrl: docResult.docViewUrl,
            certReady: docResult.ready,
          };
        } catch {
          return {
            ...item, isPa: true, pdfUrl: null, certReady: false,
          };
        }
      }
      if (!item.batchNo) {
        return {
          ...item, pdfUrl: null, certReady: false, isPa: false,
        };
      }
      try {
        const certRes = await axios.get(
          `/customer/gis/policies/certificate/pdf/batch/${item.batchNo}`,
          { validateStatus: () => true, _skipErrorRedirect: true },
        );
        const url = certRes.data?.success && certRes.data?.pdfUrl
          ? certRes.data.pdfUrl
          : null;
        if (url) {
          return {
            ...item, pdfUrl: url, certReady: true, isPa: false,
          };
        }
        if (!item.registrationNumber && item.policyNumber) {
          const docResult = await resolvePaPolicyDocument(item.policyNumber);
          if (docResult.ready && docResult.document) {
            return {
              ...item,
              isPa: true,
              policyDocument: docResult.document,
              pdfUrl: docResult.docViewUrl,
              certReady: true,
            };
          }
        }
        return {
          ...item, pdfUrl: null, certReady: false, isPa: false,
        };
      } catch {
        return {
          ...item, pdfUrl: null, certReady: false, isPa: false,
        };
      }
    },

    // Phase 2: Listing Logic
    async fetchAgentCertificates() {
      const agentCode = await this.resolveAgentCode();
      if (!agentCode) return;

      this.tableLoading = true;
      try {
        const { page, itemsPerPage } = this.tableOptions;
        const res = await axios.get(
          `/customer/quotation/policy/agent/${agentCode}/certificates`,
          {
            params: {
              page: page - 1, // backend is 0-indexed
              size: itemsPerPage,
            },
            validateStatus: () => true,
          },
        );

        if (res.status === 200 && res.data?.success) {
          const pageData = res.data.data;
          const rawItems = pageData.content || [];
          this.totalCertificates = pageData.totalElements || 0;

          // Resolve PDF availability for each item in parallel
          const resolved = await Promise.all(
            rawItems.map((item) => this.resolveCertificateAvailability(item)),
          );

          this.certificates = resolved;
        }
      } catch (err) {
        console.error('Error fetching agent certificates:', err);
      } finally {
        this.tableLoading = false;
      }
    },

    async resolveAgentCode() {
      // 1. Check local storage (populated by Dashboard)
      const cached = localStorage.getItem('agentNumericId');
      if (cached) return cached;

      // 2. Fallback: Fetch from summary API if not in storage
      const email = this.authenticatedUser?.email;
      if (!email) return null;

      try {
        const res = await axios.get(`/customer/gis/agent/dashboard/summary?email=${encodeURIComponent(email)}`, {
          validateStatus: () => true,
          _skipErrorRedirect: true,
        });
        if (res.status === 200 && res.data?.agent?.code) {
          const code = String(res.data.agent.code);
          localStorage.setItem('agentNumericId', code);
          return code;
        }
      } catch (e) {
        console.error('Failed to resolve agent code', e);
      }
      return null;
    },

    async openCertFromTable(item) {
      if (item.isPa && item.policyDocument) {
        trackAgentInteraction(this, 'certificates_open_policy_document', {
          policy_number: item?.policyNumber || 'unknown',
        });
        const openResult = await openPolicyDocument(item.policyDocument);
        if (!openResult.success && this.$toast) {
          this.$toast.error(openResult.message || 'Could not open policy document');
        }
        return;
      }
      if (item.pdfUrl) {
        trackAgentInteraction(this, 'certificates_open_from_table', {
          policy_number: item?.policyNumber || 'unknown',
        });
        window.open(item.pdfUrl, '_blank', 'noopener,noreferrer');
      }
    },

    async downloadCertFromTable(item) {
      if (item.isPa && item.policyDocument) {
        trackAgentInteraction(this, 'certificates_download_policy_document', {
          policy_number: item?.policyNumber || 'unknown',
        });
        const dlResult = await downloadPolicyDocument(item.policyDocument, item.policyNumber);
        if (!dlResult.success && this.$toast) {
          this.$toast.error(dlResult.message || 'Could not download policy document');
        }
        return;
      }
      if (!item.pdfUrl) return;
      trackAgentInteraction(this, 'certificates_download_from_table', {
        policy_number: item?.policyNumber || 'unknown',
      });
      const a = document.createElement('a');
      a.href = item.pdfUrl;
      a.download = `Certificate_${item.policyNumber}.pdf`;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } catch (e) {
        return dateStr;
      }
    },
  },
  metaInfo: { title: 'Digital Certificates' },
};
</script>

<style scoped>
.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px;
}
.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* Search Card */
.search-card {
  background: white;
  border-radius: 14px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: 24px;
}
.search-card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 16px;
}
.search-wrapper {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 0 14px;
  height: 48px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.search-wrapper.focused {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0,174,239,0.12);
  background: white;
}
.search-icon {
  color: #94A3B8;
  font-size: 18px;
  margin-right: 8px;
  flex-shrink: 0;
}
.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.925rem;
  color: #1E4276;
  background: transparent;
}
.search-input::placeholder { color: #CBD5E1; }
.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: #94A3B8;
  font-size: 16px;
  display: flex;
  align-items: center;
  padding: 2px;
}

/* Certificate Result Card */
.cert-card {
  background: white;
  border: 1px solid #E8EDF2;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.cert-card-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.cert-icon-wrap {
  width: 56px;
  height: 56px;
  background: #F0FDF4;
  border: 1.5px solid #BBF7D0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cert-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #16A34A;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 2px;
}
.cert-policy-no {
  font-size: 1.05rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px;
}
.cert-batch {
  font-size: 0.75rem;
  color: #94A3B8;
  margin: 0;
}
.cert-card-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* Info Helper */
.info-helper {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 10px;
  padding: 12px 14px;
}
.info-helper-text {
  font-size: 0.78rem;
  color: #0C4A6E;
  margin: 0;
  line-height: 1.6;
}

/* Error State */
.error-state {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 18px 20px;
  margin-top: 4px;
}
.error-icon-wrap {
  width: 44px;
  height: 44px;
  background: #FEE2E2;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.error-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #DC2626;
  margin: 0 0 4px;
}
.error-body {
  font-size: 0.8rem;
  color: #6B7280;
  margin: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  background: white;
  border-radius: 16px;
  border: 1.5px dashed #E2E8F0;
}
.empty-icon-ring {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #F8FAFC;
  border: 2px dashed #CBD5E1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: #475569;
  margin: 0 0 6px;
}
.empty-subtitle {
  font-size: 0.825rem;
  color: #94A3B8;
  margin: 0;
}

/* Transitions */
.slide-up-enter-active { transition: all 0.25s ease-out; }
.slide-up-enter { opacity: 0; transform: translateY(10px); }

/* Table Styling */
.table-card {
  border-radius: 14px !important;
  border: 1px solid #E8EDF2 !important;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05) !important;
  overflow: hidden;
}
.cert-data-table >>> th {
  background: #F8FAFC !important;
  color: #64748B !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  height: 48px !important;
}
.cert-data-table >>> td {
  height: 64px !important;
  border-bottom: 1px solid #F1F5F9 !important;
}
/* Certificate Table Shimmer */

.certificate-shimmer-table {
  width: 100%;
  padding: 0;
}

.certificate-shimmer-row {
  display: grid;
  grid-template-columns:
    minmax(150px, 1.3fr)
    minmax(100px, 1fr)
    minmax(160px, 1.3fr)
    minmax(130px, 1fr)
    minmax(130px, 1fr)
    100px;
  align-items: center;
  column-gap: 20px;
  min-height: 64px;
  padding: 0 20px;
  border-bottom: 1px solid #F1F5F9;
}

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: certificate-shimmer 1.5s infinite;
}

@keyframes certificate-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

.shimmer-policy {
  width: 145px;
  height: 17px;
}

.shimmer-reg {
  width: 90px;
  height: 14px;
}

.certificate-shimmer-client {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shimmer-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
}

.shimmer-client-name {
  width: 110px;
  height: 15px;
}

.shimmer-product {
  width: 105px;
  height: 15px;
}

.certificate-shimmer-dates {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.shimmer-date {
  width: 95px;
  height: 12px;
}

.shimmer-date-second {
  width: 85px;
}

.certificate-shimmer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shimmer-action {
  width: 30px;
  height: 30px;
  border-radius: 50%;
}
</style>
